import { lazy, type ComponentType, type LazyExoticComponent } from "react";
import { BUILD_COMMIT, UNKNOWN_COMMIT } from "./buildInfo";

/**
 * Recovery from the one failure a deploy inflicts on tabs that are already open.
 *
 * Every `wrangler deploy` replaces the Worker's asset manifest wholesale, so the
 * previous build's content-hashed chunks stop existing. A tab still holding the
 * old `index.html` knows only the old chunk names, and the first route it
 * lazy-loads after the deploy asks for a file that is gone. Cloudflare's
 * `not_found_handling = "single-page-application"` then answers with
 * `index.html` and a 200 rather than a 404, so the browser gets HTML where it
 * expected a module and `import()` rejects.
 *
 * There is nothing to retry: the old chunk is never coming back, and only a
 * fresh `index.html` names chunks that exist. So the response is to reload once
 * — which keeps `window.location.href`, landing the person on the page they
 * were navigating to rather than back at a dashboard root.
 */

/** Where the loop guard is kept. Session-scoped: a new tab starts fresh. */
const RELOAD_KEY = "gradebook:stale-deploy-reload";

/**
 * Set once a reload has actually been triggered, so the second failure of the
 * same deploy knows the page is already on its way out. Module state rather
 * than storage on purpose: it describes *this* document, and the next one
 * starts over. See `isStaleDeployReloadPending`.
 */
let reloadPending = false;

/**
 * How each engine words it when a dynamic `import()` cannot be fetched, or is
 * served something that isn't a module. Matched on text because that is all the
 * platform gives us — the rejection is a plain `TypeError` with no code, no
 * status, and no reference to the URL that failed beyond the message.
 */
const STALE_CHUNK_PATTERNS = [
  // Chrome, Edge — both the fetch failure and the 200-with-HTML case.
  "failed to fetch dynamically imported module",
  // Chrome, when the SPA fallback's HTML is rejected on MIME type instead.
  "expected a javascript module script",
  // Firefox.
  "error loading dynamically imported module",
  // Safari.
  "importing a module script failed",
  // Vite's own CSS preload helper, which fails the same way for the same reason.
  "unable to preload css",
];

/**
 * Whether `error` is a chunk that no longer exists, rather than a fault in the
 * page's own code.
 *
 * Deliberately narrow. A false positive here reloads the page under someone
 * mid-task, which is a worse outcome than showing them the ordinary error card,
 * so this matches only the specific wordings above.
 */
export function isStaleDeployError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : "";
  if (!message) return false;

  const normalised = message.toLowerCase();
  return STALE_CHUNK_PATTERNS.some((pattern) => normalised.includes(pattern));
}

export interface StaleDeployDeps {
  /** Overridden in tests; jsdom's `location.reload` cannot be spied on. */
  reload: () => void;
  /** `null` models a browser refusing storage (private mode, blocked site data). */
  storage: Pick<Storage, "getItem" | "setItem"> | null;
  /** The bundle asking to reload. See `attemptStaleDeployReload`. */
  commit: string;
}

function defaultDeps(): StaleDeployDeps {
  return {
    reload: () => window.location.reload(),
    // Reading `sessionStorage` at all can throw where site data is blocked, so
    // even acquiring the handle is guarded.
    storage: (() => {
      try {
        return window.sessionStorage;
      } catch {
        return null;
      }
    })(),
    // `UNKNOWN_COMMIT` when Vite's `define` didn't run (tests, and any bundler
    // that skips it): the guard then becomes one-shot per session, which is the
    // safe reading of "we don't know what we're running".
    commit: BUILD_COMMIT ?? UNKNOWN_COMMIT,
  };
}

/**
 * Reload the page once to pick up the current build. Returns whether a reload
 * was actually triggered.
 *
 * The loop guard is keyed on the **build commit** rather than on a bare "have we
 * tried" flag, and that distinction is what stops a genuinely broken deploy
 * reloading forever. A stale tab on commit X reloads, comes back on commit Y,
 * and the guard — which recorded X — no longer applies, so a *second* deploy
 * landing on the same tab is still recovered. But a bundle whose own chunks are
 * missing reloads into itself, finds its own commit already recorded, and stops,
 * leaving the error boundary to explain it.
 *
 * Without storage there is no guard, so we decline rather than risk the loop.
 * We also decline while the browser reports itself offline, where a reload is
 * all cost and no chance of picking up a newer build.
 */
export function attemptStaleDeployReload(
  overrides: Partial<StaleDeployDeps> = {},
): boolean {
  const { reload, storage, commit } = { ...defaultDeps(), ...overrides };
  if (!storage) return false;

  // A chunk also fails to load when the network drops, and that failure looks
  // identical from here. Reloading then cannot fetch a newer build — it throws
  // away a working app, and whatever was unsaved in it, for the browser's
  // offline page. Only the definite negative is acted on: `onLine === true`
  // means "there is an interface", not "the network works", so a genuine stale
  // deploy on a flaky connection is still recovered.
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return false;
  }

  try {
    if (storage.getItem(RELOAD_KEY) === commit) return false;
    storage.setItem(RELOAD_KEY, commit);
  } catch {
    // Storage that reads but will not write is storage we cannot build a guard
    // on, and an unguarded reload is a reload loop.
    return false;
  }

  reloadPending = true;
  reload();
  return true;
}

/**
 * Whether a reload has already been triggered and this page is on its way out.
 *
 * Exported for `lazyPage`, and because the two recovery paths race for the same
 * one-shot guard: Vite's `vite:preloadError` usually fires first and takes it,
 * and the `import()` that rejects a moment later then finds
 * `attemptStaleDeployReload` declining even though a reload *is* in flight.
 */
export function isStaleDeployReloadPending(): boolean {
  return reloadPending;
}

/**
 * Listen for Vite's `vite:preloadError`, which fires when the preload of a
 * dynamically imported chunk fails.
 *
 * `preventDefault()` stops Vite rethrowing the error into the page, since the
 * reload is the whole response to it. When the guard declines, the default is
 * left alone and the failure reaches the error boundary, which is what should
 * happen — at that point it is either a broken deploy rather than a stale one,
 * or the network is down and reloading would only make things worse.
 */
export function installStaleDeployRecovery(
  overrides: Partial<StaleDeployDeps> = {},
): void {
  window.addEventListener("vite:preloadError", (event) => {
    if (attemptStaleDeployReload(overrides)) {
      event.preventDefault();
    }
  });
}

/**
 * `React.lazy` that survives a deploy.
 *
 * Belt and braces alongside `installStaleDeployRecovery`: the event covers the
 * preload path, and this covers the import itself, which can reject without a
 * preload ever having been attempted.
 *
 * On a stale chunk it returns a promise that never settles. That looks odd, and
 * is deliberate — the reload is already in flight, and holding Suspense on its
 * loading spinner for the fraction of a second until the page goes is better
 * than flashing a crash card at someone on the way out.
 */
export function lazyPage<T extends ComponentType>(
  load: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(() =>
    load().catch((error: unknown) => {
      if (!isStaleDeployError(error)) throw error;
      // `||` and not `&&`: the preload handler has usually already taken the
      // one-shot guard for this deploy, so `attempt` returns false while a
      // reload is genuinely in flight. Rethrowing there would flash the crash
      // card for the moment before the page goes.
      if (attemptStaleDeployReload() || isStaleDeployReloadPending()) {
        return new Promise<{ default: T }>(() => {});
      }
      throw error;
    }),
  );
}
